import { useCallback, useState, useRef } from 'react'
import { Box, Typography } from '@mui/material'
import type { UUID } from 'crypto'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useStep } from '../hooks/useStep'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useFundingNeedDocs } from '@/utils/hooks/fundingNeeds'
import { useUploadFundingNeedDocs } from '../hooks/useUploadDocs'
import { COLORS } from '../theme'
import { stepHeadingSx } from '../styles'
import UploadStep from '../components/UploadStep'

export default function FundingSupportDocs() {
  const { orgUuid, fundingNeedId, maxStepReached, setStaleFrom } = useOnboardingV2()
  const { token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''

  const [isUploading, setIsUploading] = useState(false)

  const hasFundingNeed = !!fundingNeedId && !!orgUuid
  const { data: docs, deleteFundingNeedDoc } = useFundingNeedDocs(
    orgUuid as UUID,
    token,
    fundingNeedId ?? 0,
  )

  const uploadMutation = useUploadFundingNeedDocs(orgUuid, fundingNeedId, token)
  const filesChangedRef = useRef(false)

  useStep({
    isValid: !isUploading,
    beforeNext: async () => {
      if (filesChangedRef.current && maxStepReached >= STEPS.SCAN_RUNNING) {
        setStaleFrom(prev => prev === null ? STEPS.SCAN_RUNNING : Math.min(prev, STEPS.SCAN_RUNNING))
      }
    },
  })

  const handleUploadFiles = useCallback(
    async (files: File[]): Promise<Array<{ id: string }>> => {
      if (!hasFundingNeed) return []
      filesChangedRef.current = true
      return uploadMutation.mutateAsync(files)
    },
    [uploadMutation, hasFundingNeed],
  )

  const handleDeleteFile = useCallback(
    async (docId: string): Promise<void> => {
      if (!hasFundingNeed) return
      filesChangedRef.current = true
      await deleteFundingNeedDoc.mutateAsync({
        token,
        docId: Number(docId),
        needId: fundingNeedId!,
      })
    },
    [deleteFundingNeedDoc, token, fundingNeedId, hasFundingNeed],
  )

  if (!hasFundingNeed) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <Typography variant="h5" sx={{ ...stepHeadingSx, mb: 2 }}>
          Supporting documents
        </Typography>
        <Typography variant="body1" sx={{ color: COLORS.secondary }}>
          No funding need has been created yet. Continue to the next step.
        </Typography>
      </Box>
    )
  }

  return (
    <UploadStep
      title="Upload supporting documents for your funding request"
      description="Upload budgets, project plans, or other documents that support your funding request."
      descriptionVariant="body2"
      skipBanner={
        docs?.length === 0
          ? "Don't have any documents? No worries — you can skip this step."
          : undefined
      }
      skipHintBottom="No documents to upload? Click Continue to skip this step."
      docs={docs}
      onUploadFiles={handleUploadFiles}
      onDeleteFile={handleDeleteFile}
      onUploadingChange={setIsUploading}
    />
  )
}
